import { Box, Stack } from '@mui/material';
import { submitApproval } from 'lib/api/approvals';
import type { ApprovalType } from 'lib/api/approvals/types';
import { COMITEE_TYPES } from 'lib/api/approvals/types';
import {
  BackBubbleButton,
  CustomButton,
  ErrorState,
  getStatusChipColor,
  HeroHeader,
  MobileSelect,
  MobileTextArea,
} from 'lib/mobileDesignSystem';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import type { ApprovalFormState } from './types';
import {
  isApprovalFormValid,
  reasonRules,
  roleSelectRules,
} from './validation';

type ApprovalTypeParam = 'approve' | 'reject';

export interface SharedApprovalPageProps {
  approvalType: ApprovalType;
  entityId: number;
  requestId: string;
  userRoles: Array<{ id: number; name: string }>;
  onSuccess: () => void;
  isError: boolean;
}

const APPROVAL_TYPE_APPROVE: ApprovalTypeParam = 'approve';

const SharedApprovalPage: React.FC<SharedApprovalPageProps> = ({
  approvalType,
  entityId,
  requestId,
  userRoles,
  onSuccess,
  isError,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const typeFromState = (location.state as { type?: ApprovalTypeParam })?.type;
  const typeFromQuery = searchParams.get('type') as ApprovalTypeParam | null;
  const type: ApprovalTypeParam =
    typeFromState ??
    (typeFromQuery === 'reject' ? 'reject' : APPROVAL_TYPE_APPROVE);

  const [form, setForm] = useState<ApprovalFormState>({
    roleId: null,
    reason: '',
  });

  useEffect(() => {
    if (userRoles.length > 0 && form.roleId === null) {
      setForm((prev) => ({ ...prev, roleId: userRoles[0].id }));
    }
  }, [userRoles, form.roleId]);

  const { mutate: submitApprovalMutation, isPending: isSubmitting } =
    submitApproval(dispatch, approvalType, entityId, COMITEE_TYPES.RISK);

  const fadeColor =
    type === APPROVAL_TYPE_APPROVE
      ? getStatusChipColor('success')
      : getStatusChipColor('error');
  const title = `Aprobación #${requestId}`;

  const isFormValid = isApprovalFormValid(form);
  const canSubmit =
    isFormValid && !isSubmitting && entityId > 0 && userRoles.length > 0;

  const handleConfirm = () => {
    if (!canSubmit || form.roleId == null) return;
    submitApprovalMutation(
      {
        decision: type === APPROVAL_TYPE_APPROVE ? 'APPROVED' : 'REJECTED',
        roleId: form.roleId,
        message: form.reason.trim(),
      },
      { onSuccess },
    );
  };

  const handleBack = () => navigate(-1);

  if (isError) {
    return <ErrorState message="No se pudo cargar la operación." />;
  }

  return (
    <Stack sx={{ minHeight: '100%', pb: 12, px: 0 }} spacing={0}>
      <Box sx={{ position: 'relative', flexShrink: 0 }}>
        <HeroHeader
          title={title}
          minHeight={60}
          fadeColor={fadeColor}
          sx={{ width: '100vw', mx: 'calc(-50vw + 50%)' }}
        />
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            pt: 0.5,
            px: 2,
          }}
        >
          <BackBubbleButton onClick={handleBack} />
        </Box>
      </Box>

      <Stack spacing={2} sx={{ px: 2, pt: 2, flex: 1 }}>
        {userRoles.length === 0 ? (
          <ErrorState message="No hay roles disponibles para firmar esta aprobación." />
        ) : (
          <>
            <MobileSelect
              label="Firmas como:"
              placeholder="Rol de aprobación"
              options={userRoles}
              value={form.roleId}
              onChange={(roleId) => setForm((prev) => ({ ...prev, roleId }))}
              validationRules={roleSelectRules}
              required
            />
            <MobileTextArea
              label="Razón:"
              placeholder={
                type === APPROVAL_TYPE_APPROVE
                  ? 'Describe por qué apruebas esta solicitud...'
                  : 'Describe por qué rechazas esta solicitud...'
              }
              value={form.reason}
              onChange={(reason) => setForm((prev) => ({ ...prev, reason }))}
              validationRules={reasonRules}
              rows={4}
              required
            />
          </>
        )}
      </Stack>

      {userRoles.length > 0 && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            p: 2,
          }}
        >
          <CustomButton
            variant="primary"
            fullWidth
            onClick={handleConfirm}
            disabled={!canSubmit}
            loading={isSubmitting}
          >
            Confirmar
          </CustomButton>
        </Box>
      )}
    </Stack>
  );
};

export default SharedApprovalPage;
